type TelegramWebApp = {
  initData: string;
  initDataUnsafe: {
    user?: {
      id: number;
      first_name: string;
      last_name?: string;
      username?: string;
      photo_url?: string;
    };
    start_param?: string;
  };
  ready: () => void;
  expand: () => void;
  isFullscreen?: boolean;
  exitFullscreen?: () => void;
  onEvent?: (event: string, handler: () => void) => void;
  openTelegramLink?: (url: string) => void;
};

// Bot + Mini App slug for share deeplinks. Set per environment via the VITE_*
// vars so the staging build (lot_bid_bot/play) doesn't generate prod links
// that route to the wrong D1. Slug is required: `t.me/<bot>?startapp=` opens
// the bot page (extra tap) instead of launching the app directly, while
// `t.me/<bot>/<slug>?startapp=` enters the Mini App immediately.
const TG_BOT_USERNAME =
  (import.meta.env.VITE_TG_BOT_USERNAME as string | undefined) ?? 'skinstep_bot';
const TG_APP_SHORT_NAME = (import.meta.env.VITE_TG_APP_SHORT_NAME as string | undefined) ?? 'play';

declare global {
  interface Window {
    Telegram?: { WebApp?: TelegramWebApp };
  }
}

export type TgContext = {
  webApp: TelegramWebApp;
  initData: string;
};

export function getTgContext(): TgContext | null {
  const webApp = window.Telegram?.WebApp;
  if (!webApp) return null;
  if (!webApp.initData) return null;
  return { webApp, initData: webApp.initData };
}

type ShimUser = { id: number; first_name: string; username?: string };

function parseShimUser(initData: string): ShimUser | undefined {
  const userRaw = new URLSearchParams(initData).get('user');
  return userRaw ? (JSON.parse(userRaw) as ShimUser) : undefined;
}

async function fetchMintedInitData(): Promise<string | null> {
  const baseUrl = (import.meta.env.VITE_API_URL as string | undefined)?.replace(/\/$/, '') ?? '';
  const res = await fetch(`${baseUrl}/api/__dev/mint-initdata`, { method: 'POST' });
  if (!res.ok) {
    console.warn('[dev-tg-shim] mint-initdata failed', res.status);
    return null;
  }
  const { initData } = (await res.json()) as { initData: string };
  return initData;
}

export async function installDevTgShim(): Promise<void> {
  if (!import.meta.env.DEV) return;
  if (window.Telegram?.WebApp?.initData) return;

  const initData = await fetchMintedInitData();
  if (!initData) return;

  window.Telegram = {
    WebApp: {
      initData,
      initDataUnsafe: { user: parseShimUser(initData) },
      ready: () => undefined,
      expand: () => undefined,
      openTelegramLink: (url: string) => window.open(url, '_blank', 'noopener,noreferrer'),
    },
  };
}

// Best-effort fresh initData for a session re-exchange. In real Telegram the
// client injects initData once at launch (in the URL hash) with no API to
// refresh it — so a 1h-old session can only be recovered by reopening the Mini
// App. In local dev we re-mint a freshly-signed initData so an expired session
// self-heals silently, no reload prompt. Returns null when not in Telegram.
export async function refreshInitData(): Promise<string | null> {
  if (import.meta.env.DEV) {
    const minted = await fetchMintedInitData();
    const webApp = window.Telegram?.WebApp;
    if (minted && webApp) {
      webApp.initData = minted;
      webApp.initDataUnsafe = { ...webApp.initDataUnsafe, user: parseShimUser(minted) };
      return minted;
    }
  }
  return window.Telegram?.WebApp?.initData ?? null;
}

// Main Mini App launches (the "Open" button on the bot profile / chat list)
// can start in fullscreen mode, which floats a translucent header over the
// content and draws edge-to-edge past the safe areas. We always want standard
// windowed chrome (titled header + respected insets), so drop out of fullscreen
// on launch and stay out if Telegram re-applies it.
export function forceWindowedMode(webApp: TelegramWebApp): void {
  const exit = () => {
    if (webApp.isFullscreen) webApp.exitFullscreen?.();
  };
  exit();
  webApp.onEvent?.('fullscreenChanged', exit);
}

export function openTgLink(url: string): void {
  const webApp = window.Telegram?.WebApp;
  if (webApp?.openTelegramLink) {
    webApp.openTelegramLink(url);
    return;
  }
  window.open(url, '_blank', 'noopener,noreferrer');
}

export function buildLotDeeplink(lotId: string): string {
  return `https://t.me/${TG_BOT_USERNAME}/${TG_APP_SHORT_NAME}?startapp=lot_${lotId}`;
}

// Bare Mini App launch link — reopening through it hands us a freshly-signed
// initData, the only way to recover an expired session inside real Telegram.
export function buildAppDeeplink(): string {
  return `https://t.me/${TG_BOT_USERNAME}/${TG_APP_SHORT_NAME}`;
}

// Share a deeplink through Telegram's native share dialog when running inside
// TMA, or fall back to Web Share / clipboard. The receiving user lands on the
// Mini App with the deeplink's start_param; see App.tsx for the routing side.
async function shareDeeplink(deeplink: string, text: string): Promise<void> {
  const webApp = window.Telegram?.WebApp;
  if (webApp?.openTelegramLink) {
    const shareUrl = `https://t.me/share/url?url=${encodeURIComponent(deeplink)}&text=${encodeURIComponent(text)}`;
    webApp.openTelegramLink(shareUrl);
    return;
  }

  if (typeof navigator !== 'undefined' && typeof navigator.share === 'function') {
    try {
      await navigator.share({ title: 'SkinStep', text, url: deeplink });
      return;
    } catch (err) {
      // AbortError (user cancelled the picker) is expected; anything else
      // we degrade to clipboard rather than re-throw to the click handler.
      if (!(err instanceof Error) || err.name !== 'AbortError') {
        console.error('shareDeeplink.web_share_failed', err);
      } else {
        return;
      }
    }
  }

  if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(deeplink);
    } catch (err) {
      console.error('shareDeeplink.clipboard_failed', err);
    }
  }
}

export function shareLot(lotId: string, text: string): Promise<void> {
  return shareDeeplink(buildLotDeeplink(lotId), text);
}

// Personal invite link — friends land on the Mini App with start_param='ref_<code>'.
export function buildReferralDeeplink(code: string): string {
  return `https://t.me/${TG_BOT_USERNAME}/${TG_APP_SHORT_NAME}?startapp=ref_${code}`;
}

export function shareReferral(code: string, text: string): Promise<void> {
  return shareDeeplink(buildReferralDeeplink(code), text);
}

// Avatar from the Telegram launch payload. Often absent (privacy settings, dev
// shim) — callers fall back to a monogram.
export function getTgPhotoUrl(): string | null {
  return window.Telegram?.WebApp?.initDataUnsafe.user?.photo_url ?? null;
}

// Telegram passes a launch parameter through the `startapp` query on a share
// deeplink. We consume only `lot_<id>` here; `ref_<code>` is minted for invites
// but its routing is deferred, so anything else is ignored.
export function readStartLotId(): string | null {
  const param = window.Telegram?.WebApp?.initDataUnsafe.start_param;
  if (!param) return null;
  if (!param.startsWith('lot_')) return null;
  const id = param.slice(4);
  // Match lotIdSchema constraints (url-safe, 8..64 chars) to keep callers safe
  // from a hostile start_param that just happens to be valid `tg://`.
  if (!/^[a-z0-9_-]{8,64}$/i.test(id)) return null;
  return id;
}
